import React, {useCallback, useMemo, useState, useEffect } from "react";
import InnerPageStructure from "../../../shared/components/generic/inner-page-structure/inner-page-structure";
import ModuleTabs from "../../../shared/components/generic/module-tabs/module-tabs";
import AssessmentLayout from "../components/assessment-layout/assessment-layout";
import AssessmentInfoBar from "../components/assessment-info-bar/assessment-info-bar";
import AssessmentSummaryCards from "../components/assessment-summary-cards/assessment-summary-cards";
import AssessmentTable from "../components/assessment-table/assessment-table";
import AssessmentReviewCard from "../components/assessment-review-card/assessment-review-card";
import AssessmentRulesModal from "../components/assessment-rules/assessment-rules-modal/assessment-rules-modal";
import InlineAlert from "../../../shared/components/generic/alert/inline-alert/inline-alert";
import { apuracaoColumnsConfig } from "../config/apuracao-columns-config";
import {
    apuracaoFilterConfig
} from "../config/apuracao-filter-config";
import { apuracaoHistoryFilterConfig } from "../config/apuracao-history-filter-config";
import PageLoading from "../../../shared/components/generic/page-loading/page-loading";
import { showCommonAlert } from "../../../shared/components/generic/alert/utils/show-common-alert-utils";
import { useAssessmentsExport } from "../hooks/useAssessmentsExport";
import { useAssessments } from "../hooks/useAssessments";
import { useAssessmentsHistory } from "../hooks/useAssessmentsHistory";
import { useOngoingAssessmentCycles } from "../hooks/useOngoingAssessmentCycles";
import { useAssessmentDecision } from "../hooks/useAssessmentDecision";
import { useSegmentations } from "../../admin/hooks/segmentacao/useSegmentations";
import {
    validateAssessmentFilters
} from "../utils/installments-assessment-utils";
import {
    hasValidationErrors
} from "../../utils/validation-utils";
import DateUtils from "../../../shared/utils/DateUtils";
import {
    useAssessmentsHistoryExport
} from "../hooks/useAssessmentsHistoryExport";
import {
	buildApprovalSummaryCards,
	buildHistorySummaryCards,
	buildClientSizeOptions,
	withClientSizeOptions
} from "../utils/assessment-view-utils";
import "./apuracao.css";

const apuracaoTabs = [
    {
        key: "approval",
        label: "Aprovação de Apuração"
    },
    {
        key: "history",
        label: "Histórico de Apuração"
    }
];

const SEGMENTATIONS_SELECT_PAGE_SIZE = 9999;

const ApuracaoContent = () => {
    const [activeTab, setActiveTab] = useState("approval");
    const [currentPage, setCurrentPage] = useState(0);
    const [historyCurrentPage, setHistoryCurrentPage] = useState(0);
    const [areSummaryPanelsVisible, setAreSummaryPanelsVisible] = useState(true);
    const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
    const [selectedRulesRow, setSelectedRulesRow] = useState(null);
    const [approvalFilters, setApprovalFilters] = useState({});
    const [historyFilters, setHistoryFilters] = useState({});
    const [approvalFilterResetKey, setApprovalFilterResetKey] = useState(0);
    const [historyFilterResetKey, setHistoryFilterResetKey] = useState(0);
    const [approvalFilterErrors, setApprovalFilterErrors] = useState({});
    const [historyFilterErrors, setHistoryFilterErrors] = useState({});

    const {
        assessments: approvalAssessments,
        pagination: approvalPagination,
        statistics: approvalStatistics,
        loading: approvalLoading,
        error: approvalError,
        refetch: fetchApprovalAssessments
    } = useAssessments();

    const {
        assessments: historyAssessments,
        pagination: historyPagination,
        statistics: historyStatistics,
        loading: historyLoading,
        error: historyError,
        refetch: fetchHistoryAssessments
    } = useAssessmentsHistory();

    const {
        cycles: ongoingCycles,
        loading: ongoingCyclesLoading,
        error: ongoingCyclesError,
        refetch: fetchOngoingCycles
    } = useOngoingAssessmentCycles();

    const {
        loading: assessmentsExportLoading,
        exportAssessments
    } = useAssessmentsExport();

    const {
        loading: historyExportLoading,
        exportAssessmentsHistory
    } = useAssessmentsHistoryExport();

    const {
        loading: decisionLoading,
        decideAssessments
    } = useAssessmentDecision();

    const {
        segmentations,
        loading: segmentationsLoading,
        error: segmentationsError,
        refetch: fetchSegmentations
    } = useSegmentations();

    useEffect(() => {
        fetchSegmentations({
            page: 0,
            size: SEGMENTATIONS_SELECT_PAGE_SIZE
        });
    }, [fetchSegmentations]);

    const selectedCycle = ongoingCycles[0] || null;

    const approvalCycleId = selectedCycle?.cycleId ?? null;

    const discountMonthsLabel = (
        selectedCycle?.discountMonthsLabel || ""
    ).toUpperCase();

    const firstApprovalDate = approvalAssessments[0]?.assessmentDate;

    const approvalAssessmentDate =
        !approvalLoading && !approvalError && firstApprovalDate
            ? DateUtils.formatDateToBrazilian(firstApprovalDate)
            : null;
        
    useEffect(() => {
        if (
            activeTab !== "approval" ||
            approvalCycleId === null
        ) {
            return;
        }

        fetchApprovalAssessments({
            cycleId: approvalCycleId,
            page: currentPage,
            size: 10,
            filters: approvalFilters
        });
    }, [
        activeTab,
        approvalCycleId,
        currentPage,
        approvalFilters,
        fetchApprovalAssessments
    ]);

    useEffect(() => {
        if (activeTab !== "history") {
            return;
        }

        fetchHistoryAssessments({
            page: historyCurrentPage,
            size: 50,
            filters: historyFilters
        });
    }, [
        activeTab,
        historyCurrentPage,
        historyFilters,
        fetchHistoryAssessments
    ]);

    const approvalSummaryCards = useMemo(
        () => buildApprovalSummaryCards(approvalStatistics),
        [approvalStatistics]
    );

    const historySummaryCards = useMemo(
        () => buildHistorySummaryCards(historyStatistics),
        [historyStatistics]
    );

    const clientSizeOptions = useMemo(
        () => buildClientSizeOptions(segmentations),
        [segmentations]
    );

    useEffect(() => {
        fetchOngoingCycles();
    }, [fetchOngoingCycles]);

    const handleOpenRulesModal = useCallback(row => {
        setSelectedRulesRow(row);
    }, []);

    const handleCloseRulesModal = useCallback(() => {
        setSelectedRulesRow(null);
    }, []);

    const apuracaoColumns = useMemo(
        () =>
            apuracaoColumnsConfig({
                onViewRules: handleOpenRulesModal
            }),
        [handleOpenRulesModal]
    );

    const apuracaoHistoryColumns = useMemo(
        () =>
            apuracaoColumnsConfig({
                onViewRules: handleOpenRulesModal,
                showStatus: true
            }),
        [handleOpenRulesModal]
    );


    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
    };

    const handleHistoryPageChange = ({ selected }) => {
        setHistoryCurrentPage(selected);
    };

    const handleFilterSubmit = async values => {
        const isApproval = activeTab === "approval";

        const errors = validateAssessmentFilters(values, {
            includePeriod: !isApproval
        });

        if (isApproval) {
            setApprovalFilterErrors(errors);
        } else {
            setHistoryFilterErrors(errors);
        }

        if (hasValidationErrors(errors)) {
            await showCommonAlert({
                title: "Filtros inválidos",
                message: Object.values(errors).join("\n"),
                icon: "warning"
            });

            return;
        }

        if (isApproval) {
            setApprovalFilters(values);
            setCurrentPage(0);
            return;
        }

        setHistoryFilters(values);
        setHistoryCurrentPage(0);
    };

    const handleFilterFieldChange = ({ name }) => {
        const setErrors = activeTab === "approval"
            ? setApprovalFilterErrors
            : setHistoryFilterErrors;

        setErrors(previous => {
            if (!previous[name]) {
                return previous;
            }

            const nextErrors = { ...previous };
            delete nextErrors[name];

            return nextErrors;
        });
    };

    const handleClearFilters = () => {
        if (activeTab === "approval") {
            setApprovalFilterErrors({});
            setApprovalFilters({});
            setCurrentPage(0);
            setApprovalFilterResetKey(previous => previous + 1);
            return;
        }

        setHistoryFilterErrors({});
        setHistoryFilters({});
        setHistoryCurrentPage(0);
        setHistoryFilterResetKey(previous => previous + 1);
    };

    const handleToggleSummaryPanels = () => {
        setAreSummaryPanelsVisible(previousValue => !previousValue);
    };

    const handleToggleFilterPanel = () => {
        setIsFilterPanelVisible(previousValue => !previousValue);
    };

    const handleExportAssessments = async ({
        cycleId,
        filters = {},
        fileName = "apuracoes-parcelas.csv"
    } = {}) => {
        const result = await exportAssessments({
            cycleId,
            filters,
            fileName
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível exportar as apurações.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });
        }
    };

    const handleExportAssessmentsHistory = async () => {
        const result = await exportAssessmentsHistory({
            filters: historyFilters
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível exportar o histórico.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });
        }
    };

    const approveCycle = () => {
        return decideAssessments({
            cycleId: approvalCycleId,
            status: "APROVADO"
        });
    };

    const rejectCycle = (values = {}) => {
        return decideAssessments({
            cycleId: approvalCycleId,
            status: "REPROVADO",
            rejectionDescription: values.reviewDetails
        });
    };

    const hasApprovalData = approvalAssessments.length > 0;
    const hasHistoryData = historyAssessments.length > 0;

    const isApprovalEmpty = Boolean(
        selectedCycle &&
        !approvalLoading &&
        !approvalError &&
        !hasApprovalData
    );

    const isHistoryEmpty =
        !historyLoading &&
        !historyError &&
        historyAssessments.length === 0;

    const isClientSizeDisabled = segmentationsLoading || Boolean(segmentationsError);

    const approvalFilterFields = useMemo(() => {
        return withClientSizeOptions(
            apuracaoFilterConfig,
            clientSizeOptions,
            isClientSizeDisabled
        );
    }, [clientSizeOptions, isClientSizeDisabled]);

    const historyFilterFields = useMemo(() => {
        return withClientSizeOptions(
            apuracaoHistoryFilterConfig,
            clientSizeOptions,
            isClientSizeDisabled
        );
    }, [clientSizeOptions, isClientSizeDisabled]);

    const innerPageElements = {
        backLabel: [{ label: "Apuração" }],
        title: "Apuração Parcelas"
    };

    return (
        <InnerPageStructure innerPageElements={innerPageElements}>
            <PageLoading
                states={[
                    {
                        loading: activeTab === "approval" && approvalLoading,
                        message: "Carregando apurações..."
                    },
                    {
                        loading: activeTab === "history" && historyLoading,
                        message: "Carregando histórico de apurações..."
                    },
                    {
                        loading: assessmentsExportLoading,
                        message: "Exportando apurações..."
                    },
                    {
                        loading: historyExportLoading,
                        message: "Exportando histórico de apurações..."
                    }
                ]}
            />
            <ModuleTabs
                tabs={apuracaoTabs}
                activeKey={activeTab}
                onChange={setActiveTab}
                className="apuracao__tabs"
            />

            {activeTab === "approval" && (
                <AssessmentLayout
                    isEmpty={isApprovalEmpty}
                    hasError={Boolean(approvalError || ongoingCyclesError)}
                    filterVisible={isFilterPanelVisible}
                    filterTitle="Filtros de Apuração"
                    filterFormId="apuracao-filter-form"
                    filterFields={approvalFilterFields}
                    filterResetKey={approvalFilterResetKey}
                    onFilterSubmit={handleFilterSubmit}
                    onFilterClear={handleClearFilters}
                    filterErrors={approvalFilterErrors}
                    onFilterFieldChange={handleFilterFieldChange}
                >
                    <AssessmentInfoBar
                        assessmentDate={approvalAssessmentDate}
                        discountMonths={discountMonthsLabel}
                        totalClients={
                            approvalLoading
                                ? null
                                : approvalStatistics?.totalClientes
                        }
                        totalEquipments={
                            approvalLoading
                                ? null
                                : approvalStatistics?.totalEquipamentos
                        }
                        panelsVisible={areSummaryPanelsVisible}
                        filterVisible={isFilterPanelVisible}
                        onTogglePanels={handleToggleSummaryPanels}
                        onToggleFilter={handleToggleFilterPanel}
                    />

                    {ongoingCyclesError && (
                        <InlineAlert
                            variant="warning"
                            message={ongoingCyclesError}
                        />
                    )}

                    {!ongoingCyclesLoading &&
                        !ongoingCyclesError &&
                        ongoingCycles.length === 0 && (
                            <InlineAlert
                                variant="info"
                                message="Nenhum ciclo em andamento disponível."
                            />
                        )}

                    {selectedCycle &&
                        areSummaryPanelsVisible &&
                        !approvalLoading &&
                        !approvalError &&
                        hasApprovalData &&
                        approvalStatistics && (
                            <AssessmentSummaryCards
                                cards={approvalSummaryCards}
                            />
                        )}

                    {selectedCycle && approvalError && (
                        <InlineAlert
                            variant="warning"
                            message={approvalError}
                        />
                    )}

                    {selectedCycle &&
                        !approvalLoading &&
                        !approvalError &&
                        !hasApprovalData && (
                            <InlineAlert
                                variant="info"
                                className="apuracao__empty-alert"
                                message="Nenhuma apuração encontrada para o ciclo selecionado."
                            />
                        )}

                    {selectedCycle &&
                        !approvalLoading &&
                        !approvalError &&
                        hasApprovalData && (
                            <AssessmentTable
                                columns={apuracaoColumns}
                                data={approvalAssessments}
                                rowKey="rowKey"
                                pageCount={approvalPagination.totalPages}
                                currentPage={currentPage}
                                onPageChange={handlePageChange}
                                onExport={() =>
                                    handleExportAssessments({
                                        cycleId: approvalCycleId,
                                        filters: approvalFilters,
                                        fileName: "apuracao-aprovacao.csv"
                                    })
                                }
                            />
                        )}

                    {selectedCycle &&
                        !approvalLoading &&
                        !approvalError &&
                        hasApprovalData && (
                            <AssessmentReviewCard
                                onApprove={approveCycle}
                                onReject={rejectCycle}
                                loading={decisionLoading}
                            />
                        )}
                </AssessmentLayout>
            )}

            {activeTab === "history" && (
                <AssessmentLayout
                    isEmpty={isHistoryEmpty}
                    hasError={Boolean(historyError)}
                    filterVisible={isFilterPanelVisible}
                    filterTitle="Filtros de Histórico"
                    filterFormId="apuracao-history-filter-form"
                    filterFields={historyFilterFields}
                    filterResetKey={historyFilterResetKey}
                    onFilterSubmit={handleFilterSubmit}
                    onFilterClear={handleClearFilters}
                    filterErrors={historyFilterErrors}
                    onFilterFieldChange={handleFilterFieldChange}
                >
                    <AssessmentInfoBar
                        totalClients={
                            historyLoading
                                ? null
                                : historyStatistics?.totalClientes
                        }
                        totalEquipments={
                            historyLoading
                                ? null
                                : historyStatistics?.totalEquipamentos
                        }
                        panelsVisible={areSummaryPanelsVisible}
                        filterVisible={isFilterPanelVisible}
                        onTogglePanels={handleToggleSummaryPanels}
                        onToggleFilter={handleToggleFilterPanel}
                    />

                    {areSummaryPanelsVisible &&
                        !historyLoading &&
                        !historyError &&
                        hasHistoryData &&
                        historyStatistics && (
                            <AssessmentSummaryCards
                                cards={historySummaryCards}
                            />
                        )}

                    {historyError && (
                        <InlineAlert
                            variant="warning"
                            message={historyError}
                        />
                    )}

                    {!historyLoading &&
                        !historyError &&
                        historyAssessments.length === 0 && (
                            <InlineAlert
                                variant="info"
                                className="apuracao__empty-alert"
                                message="Nenhuma apuração encontrada no histórico."
                            />
                        )}

                    {!historyLoading && !historyError && hasHistoryData && (
                        <AssessmentTable
                            columns={apuracaoHistoryColumns}
                            data={historyAssessments}
                            rowKey="rowKey"
                            pageCount={historyPagination.totalPages}
                            currentPage={historyCurrentPage}
                            onPageChange={handleHistoryPageChange}
                            onExport={handleExportAssessmentsHistory}
                        />
                    )}
                </AssessmentLayout>
            )}

            <AssessmentRulesModal
                open={Boolean(selectedRulesRow)}
                row={selectedRulesRow}
                onClose={handleCloseRulesModal}
            />
        </InnerPageStructure>
    );
};

export default ApuracaoContent;